import { useEffect, useState } from "react";
import CoachLayout from "@/components/CoachLayout";
import api from "@/lib/api";
import { LineChart, Loader2, Cpu, Users, Coins, Gauge } from "lucide-react";
import { toast } from "sonner";

export default function AdminAiCost() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [caps, setCaps] = useState({ essentiel: "", premium: "" });
  const [savingCaps, setSavingCaps] = useState(false);

  useEffect(() => {
    api.get("/admin/ai-usage").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/admin/ai-caps").then((r) => setCaps({ essentiel: r.data.caps.essentiel, premium: r.data.caps.premium })).catch(() => {});
  }, []);

  const saveCaps = async () => {
    setSavingCaps(true);
    try {
      const r = await api.post("/admin/ai-caps", { essentiel: parseInt(caps.essentiel), premium: parseInt(caps.premium) });
      setCaps({ essentiel: r.data.caps.essentiel, premium: r.data.caps.premium });
      toast.success("Plafonds enregistrés");
    } catch { toast.error("Erreur d'enregistrement"); }
    finally { setSavingCaps(false); }
  };

  const usd = (v) => `$${Number(v || 0).toFixed(4)}`;

  return (
    <CoachLayout>
      <div data-testid="admin-aicost-page" className="max-w-4xl">
        <div className="flex items-center gap-2.5">
          <LineChart size={22} className="text-sage" />
          <h1 className="text-2xl font-medium">Coût IA</h1>
        </div>
        <p className="mt-2 text-sm text-obsidian/55">Suivi de la consommation Claude : appels, tokens estimés et coût par client, par type d'appel et par modèle.</p>

        <div data-testid="ai-caps-block" className="mt-6 rounded-2xl border border-border bg-white p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-obsidian/70"><Gauge size={16} className="text-sage" /> Plafonds d'appels IA par jour et par client</div>
          <p className="mt-1 text-xs text-obsidian/50">Au-delà du plafond, MAI répond un message clair au client sans nouvel appel (aucun blocage brutal, aucune boucle).</p>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs text-obsidian/50">Essentiel</label>
              <input data-testid="cap-essentiel" type="number" value={caps.essentiel} onChange={(e) => setCaps({ ...caps, essentiel: e.target.value })}
                className="mt-1 w-28 rounded-sm border border-border px-3 py-2 text-sm tabnum focus:ring-1 focus:ring-obsidian focus:outline-none" />
            </div>
            <div>
              <label className="text-xs text-obsidian/50">Premium</label>
              <input data-testid="cap-premium" type="number" value={caps.premium} onChange={(e) => setCaps({ ...caps, premium: e.target.value })}
                className="mt-1 w-28 rounded-sm border border-border px-3 py-2 text-sm tabnum focus:ring-1 focus:ring-obsidian focus:outline-none" />
            </div>
            <button data-testid="cap-save" onClick={saveCaps} disabled={savingCaps}
              className="rounded-sm bg-obsidian text-white px-4 py-2 text-sm disabled:opacity-60">{savingCaps ? "…" : "Enregistrer"}</button>
          </div>
        </div>

        {loading ? (
          <div className="mt-10 flex justify-center"><Loader2 className="animate-spin text-obsidian/40" /></div>
        ) : !data ? (
          <p className="mt-8 text-sm text-obsidian/50">Aucune donnée d'usage pour le moment.</p>
        ) : (
          <>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat Icon={Coins} label="Coût total" value={usd(data.total_cost_usd)} testid="stat-total-cost" />
              <Stat Icon={Cpu} label="Appels IA" value={data.total_calls} />
              <Stat Icon={Users} label="Coût moyen / client" value={usd(data.avg_cost_per_client_usd)} testid="stat-avg-client" />
              <Stat Icon={Cpu} label="Tokens (in/out)" value={`${data.total_tokens_in}/${data.total_tokens_out}`} />
            </div>

            <Section title="Par type d'appel">
              <Table rows={data.by_type} cols={[["type", "Type"], ["calls", "Appels"], ["cost", "Coût", usd]]} />
            </Section>
            <Section title="Par modèle">
              <Table rows={data.by_model} cols={[["model", "Modèle"], ["calls", "Appels"], ["cost", "Coût", usd]]} />
            </Section>
            <Section title="Top clients (coût cumulé)">
              <Table rows={data.top_clients} cols={[["user_id", "Client"], ["cost", "Coût", usd]]} />
            </Section>
          </>
        )}
      </div>
    </CoachLayout>
  );
}

function Stat({ Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-border bg-white p-4">
      <Icon size={16} className="text-obsidian/40" />
      <div className="mt-2 text-xl font-semibold tabnum">{value}</div>
      <div className="text-xs text-obsidian/50 mt-0.5">{label}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-7">
      <h2 className="text-sm font-semibold text-obsidian/70 mb-2">{title}</h2>
      <div className="rounded-2xl border border-border bg-white overflow-hidden">{children}</div>
    </div>
  );
}

function Table({ rows, cols }) {
  if (!rows || rows.length === 0) return <div className="p-4 text-sm text-obsidian/40">Aucune donnée.</div>;
  return (
    <table className="w-full text-sm">
      <thead><tr className="border-b border-border text-obsidian/50">
        {cols.map((c) => <th key={c[0]} className="text-left font-medium px-4 py-2.5">{c[1]}</th>)}
      </tr></thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-border/60 last:border-0">
            {cols.map((c) => <td key={c[0]} className="px-4 py-2.5 tabnum">{c[2] ? c[2](r[c[0]]) : r[c[0]]}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
